import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { swap } from "utils";

import { SetClientWorkflowTasks } from "data/hub/clientWorkflows/actions";

import { useFeatures } from "components/FeatureFlag";

import {
  HubContext,
  ToasterContext,
} from "scenes/Hubly/components/Workspace/Provider";

import { useClientCardTasksQuery, useSubscribeToClientCardTasks } from ".";

export function useClientCardTasks(clientWorkflowId) {
  const dispatch = useDispatch();
  const [cwfId, setCwfId] = useState(clientWorkflowId);

  const commentFilterFlag = useFeatures("comment-filters", undefined);

  const { reloadData, loading } = useClientCardTasksQuery(
    clientWorkflowId,
    useCallback(
      (cwId, tasksGroup, numWorkflowComments) => {
        dispatch(SetClientWorkflowTasks(cwId, tasksGroup, numWorkflowComments));
      },
      [dispatch]
    ),
    commentFilterFlag
  );

  useSubscribeToClientCardTasks(
    useCallback(() => {
      reloadData();
    }, [reloadData])
  );

  useEffect(() => {
    if (commentFilterFlag) {
      setCwfId(undefined); // unset to trigger reload
    }
  }, [commentFilterFlag]);

  useEffect(() => {
    if (cwfId !== clientWorkflowId && commentFilterFlag !== undefined) {
      setCwfId(clientWorkflowId);
      reloadData();
    }
  }, [cwfId, clientWorkflowId, reloadData, commentFilterFlag]);

  const { reOrderTask } = useContext(HubContext);
  const { fireError } = useContext(ToasterContext);

  const clientWorkflow = useSelector(({ hubly }) => {
    return hubly.data.hub.clientWorkflows[clientWorkflowId];
  });
  const tasksGroup = clientWorkflow?.tasksGroup || [];
  const numWfComments = clientWorkflow?.numWorkflowComments || 0;

  const handleOnTaskReorder = async ({ destination, source }) => {
    if (!destination || destination.index === source.index) {
      return;
    }

    const unorderedTasksGroup = tasksGroup;

    const orderedTasksGroup = swap(tasksGroup, source.index, destination.index);

    try {
      dispatch(
        SetClientWorkflowTasks(
          clientWorkflowId,
          orderedTasksGroup,
          numWfComments
        )
      );

      await reOrderTask({
        items: orderedTasksGroup.flatMap((taskGroup) => {
          return taskGroup?.task ? taskGroup?.task : taskGroup;
        }),
      });
    } catch (error) {
      fireError("Failed to reorder task", error);
      dispatch(
        SetClientWorkflowTasks(
          clientWorkflowId,
          unorderedTasksGroup,
          numWfComments
        )
      );
    }
  };

  return {
    tasksGroup: tasksGroup,
    handleOnTaskReorder: handleOnTaskReorder,
    loading: loading && !tasksGroup?.length,
  };
}

export default useClientCardTasks;
